/**
 * Activity types: pure helpers (no DOM, no network).
 *
 * Stored units: duration in SECONDS, distance in METERS. Everything the user
 * types or reads goes through the parse/format helpers below.
 */
import { getLocale, t } from "../i18n";

export type ExerciseType = "strength" | "timed" | "distance" | "session";

export const EXERCISE_TYPES: readonly ExerciseType[] = [
  "strength",
  "timed",
  "distance",
  "session",
] as const;

export function isExerciseType(v: unknown): v is ExerciseType {
  return typeof v === "string" && (EXERCISE_TYPES as readonly string[]).includes(v);
}

export function normalizeType(v: unknown): ExerciseType {
  return isExerciseType(v) ? v : "strength";
}

/** Same caps as the backend validator. */
export const MAX_DURATION_SEC = 7 * 24 * 3600;
export const MAX_DISTANCE_M = 10_000_000;
export const MAX_ROUNDS = 10_000;

/**
 * How "a:bb" is read: `hm` = hours:minutes ("1:30" is 1 h 30 min),
 * `ms` = minutes:seconds ("1:30" is 1 min 30 s). A bare number is always minutes.
 */
export type DurationMode = "hm" | "ms";

export type ParseError = "empty" | "format" | "minutes" | "seconds" | "range";

export type ParseResult =
  | { ok: true; value: number }
  | { ok: false; error: ParseError };

const fail = (error: ParseError): ParseResult => ({ ok: false, error });

/** Parse a typed duration into whole seconds. */
export function parseDuration(raw: string, mode: DurationMode): ParseResult {
  const s = raw.trim();
  if (!s) return fail("empty");

  let sec: number;
  let m: RegExpMatchArray | null;
  if (/^\d+$/.test(s)) {
    sec = Number(s) * 60;
  } else if ((m = s.match(/^(\d+):(\d{2})$/))) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (b > 59) return fail(mode === "ms" ? "seconds" : "minutes");
    sec = mode === "ms" ? a * 60 + b : a * 3600 + b * 60;
  } else if ((m = s.match(/^(\d+):(\d{2}):(\d{2})$/))) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    const ss = Number(m[3]);
    if (min > 59) return fail("minutes");
    if (ss > 59) return fail("seconds");
    sec = h * 3600 + min * 60 + ss;
  } else {
    return fail("format");
  }

  if (!Number.isFinite(sec) || sec < 1 || sec > MAX_DURATION_SEC) {
    return fail("range");
  }
  return { ok: true, value: sec };
}

/** Text to put back into the field for a stored value (round-trips through parseDuration). */
export function durationToInput(sec: number | null | undefined, mode: DurationMode): string {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return "";
  const total = Math.round(sec);
  if (total % 60 === 0) return String(total / 60);
  const h = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const p2 = (n: number) => String(n).padStart(2, "0");
  if (mode === "ms" && h === 0) return `${mm}:${p2(ss)}`;
  return `${h}:${p2(mm)}:${p2(ss)}`;
}

/** "1 ч 30 мин", "34 мин 40 сек", "50 сек". Empty for no value. */
export function formatSeconds(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return "";
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} ${t("UNIT_HOURS")}`);
  if (m) parts.push(`${m} ${t("UNIT_MINUTES")}`);
  if (s) parts.push(`${s} ${t("UNIT_SECONDS")}`);
  return parts.join(" ");
}

/** Parse a distance in whole meters ("1500", "1 500"). No unit conversion. */
export function parseDistance(raw: string): ParseResult {
  const s = raw.replace(/\s+/g, "");
  if (!s) return fail("empty");
  if (!/^\d+$/.test(s)) return fail("format");
  const n = Number(s);
  if (!Number.isFinite(n) || n < 1 || n > MAX_DISTANCE_M) return fail("range");
  return { ok: true, value: n };
}

export function formatDistance(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m) || m <= 0) return "";
  return `${Math.round(m)} ${t("UNIT_METERS")}`;
}

/** Totals: "850 м" below a kilometer, "12,5 км" / "12.5 km" above (one decimal at most). */
export function formatDistanceTotal(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m) || m <= 0) return "";
  if (m < 1000) return formatDistance(m);
  const km = Math.round(m / 100) / 10;
  const text = getLocale() === "en" ? String(km) : String(km).replace(".", ",");
  return `${text} ${t("UNIT_KM")}`;
}

/** Message for a failed parse, in the current language. */
export function parseErrorText(error: ParseError, mode: DurationMode | "distance"): string {
  switch (error) {
    case "empty":
      return mode === "distance" ? t("ERR_DISTANCE_REQUIRED") : t("ERR_DURATION_REQUIRED");
    case "seconds":
      return t("ERR_SECONDS_MAX");
    case "range":
      return t("ERR_VALUE_RANGE");
    default:
      return mode === "ms"
        ? t("ERR_FORMAT_MS")
        : mode === "distance"
          ? t("ERR_FORMAT_DISTANCE")
          : t("ERR_FORMAT_HM");
  }
}

// ── exercise rows ───────────────────────────────────────────────────────────

/** The fields of an exercise row that depend on its type. */
export interface ActivityValues {
  exerciseType?: ExerciseType;
  sets?: number | null;
  reps?: number | null;
  weight?: number | null;
  durationSec?: number | null;
  distanceM?: number | null;
  intensity?: number | null;
  rounds?: number | null;
}

/** Type of a stored row; rows without an explicit type are inferred from their values. */
export function rowType(ex: ActivityValues): ExerciseType {
  if (isExerciseType(ex.exerciseType)) return ex.exerciseType;
  if (ex.distanceM != null) return "distance";
  if (ex.intensity != null || ex.rounds != null) return "session";
  if (ex.durationSec != null && ex.reps == null) return "timed";
  return "strength";
}

export function intensityOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 10 ? n : null;
}

export function positiveIntOrNull(v: unknown, max = Number.MAX_SAFE_INTEGER): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= max ? n : null;
}

export function typeLabel(type: ExerciseType): string {
  switch (type) {
    case "strength":
      return t("TYPE_STRENGTH");
    case "timed":
      return t("TYPE_TIMED");
    case "distance":
      return t("TYPE_DISTANCE");
    case "session":
      return t("TYPE_SESSION");
  }
}

/**
 * Value parts of a row in display order, e.g. ["3×8", "60 кг"] or
 * ["1500 м", "34 мин 40 сек"]. Strength rows match the pre-step-1 output.
 */
export function metricParts(ex: ActivityValues): string[] {
  const parts: string[] = [];
  switch (rowType(ex)) {
    case "strength":
      parts.push(`${ex.sets ?? 1}×${ex.reps ?? 1}`);
      if (ex.weight != null) parts.push(`${ex.weight} ${t("KG")}`);
      break;
    case "timed":
      push(parts, formatSeconds(ex.durationSec));
      break;
    case "distance":
      push(parts, formatDistance(ex.distanceM));
      push(parts, formatSeconds(ex.durationSec));
      break;
    case "session":
      push(parts, formatSeconds(ex.durationSec));
      if (ex.intensity != null) {
        parts.push(`${t("INTENSITY").toLowerCase()} ${ex.intensity}`);
      }
      if (ex.rounds != null) parts.push(`${t("ROUNDS").toLowerCase()} ${ex.rounds}`);
      break;
  }
  return parts;
}

function push(parts: string[], s: string) {
  if (s) parts.push(s);
}

/** Short one-line summary: "Бассейн: 1500 м, 35 мин". */
export function summarizeExercise(ex: ActivityValues & { name: string }): string {
  const parts = metricParts(ex);
  return parts.length ? `${ex.name}: ${parts.join(", ")}` : ex.name;
}

/** Which required value of a row is missing (null = row is complete). */
export type RowIssue = "duration" | "distance" | null;

export function rowIssue(
  ex: ActivityValues & { invalid?: { duration?: boolean; distance?: boolean } },
): RowIssue {
  if (ex.invalid?.distance) return "distance";
  if (ex.invalid?.duration) return "duration";
  switch (rowType(ex)) {
    case "timed":
    case "session":
      return ex.durationSec ? null : "duration";
    case "distance":
      return ex.distanceM ? null : "distance";
    default:
      return null;
  }
}

/** Plural-aware "2 ошибки" / "1 error". */
export function errorCountText(n: number): string {
  if (getLocale() === "en") return `${n} ${n === 1 ? t("ERROR_ONE") : t("ERROR_MANY")}`;
  const m10 = n % 10;
  const m100 = n % 100;
  const key =
    m10 === 1 && m100 !== 11
      ? "ERROR_ONE"
      : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)
        ? "ERROR_FEW"
        : "ERROR_MANY";
  return `${n} ${t(key)}`;
}

// ── "last time" hint and calendar dots (step 2) ─────────────────────────────

/**
 * One-line hint of the previous result for a non-strength row, e.g.
 * "в прошлый раз: 1 ч 30 мин, интенсивность 8". History is newest first; the
 * first entry with a value for this type wins. Empty string = nothing to show.
 */
export function lastTimeHint(
  history: readonly ActivityValues[] | null | undefined,
  type: ExerciseType,
): string {
  if (type === "strength") return "";
  for (const h of history ?? []) {
    const parts = metricParts({ ...h, exerciseType: type });
    if (parts.length) return t("LAST_TIME").replace("{v}", parts.join(", "));
  }
  return "";
}

/** Distinct known types of a calendar day in fixed order; unknown values are dropped. */
export function calendarTypes(raw: unknown): ExerciseType[] {
  if (!Array.isArray(raw)) return [];
  return EXERCISE_TYPES.filter((type) => raw.includes(type));
}
